/**
 * The editable copy of a Made-to-Measure order: immutable updates of the order,
 * its props, product flags, parts and measurements, the form check and the
 * payload sent back.
 */
import type { Order } from '../api';
import { isTruthyFlag } from '../details-logic';
import {
  orderParts,
  partSizePattern,
  type Measurement,
  type OrderPart,
} from './dms-logic';

export type DmsDraft = Order;

const patchRecord = (
  record: Record<string, unknown> | null | undefined,
  patch: Record<string, unknown>,
): Record<string, unknown> => ({ ...(record ?? {}), ...patch });

export const patchOrder = (
  draft: DmsDraft,
  patch: Partial<Order>,
): DmsDraft => ({
  ...draft,
  ...patch,
});

export const patchProps = (
  draft: DmsDraft,
  patch: Record<string, unknown>,
): DmsDraft => ({ ...draft, props: patchRecord(draft.props, patch) });

export const patchProduct = (
  draft: DmsDraft,
  patch: Record<string, unknown>,
): DmsDraft => ({ ...draft, product: patchRecord(draft.product, patch) });

export const patchClient = (
  draft: DmsDraft,
  patch: Record<string, unknown>,
): DmsDraft => ({
  ...draft,
  client: patchRecord(
    draft.client as Record<string, unknown> | undefined,
    patch,
  ),
});

export const patchPart = (
  draft: DmsDraft,
  partId: string,
  update: (part: OrderPart) => OrderPart,
): DmsDraft => ({
  ...draft,
  parts: orderParts(draft).map((part) =>
    part.id === partId ? update(part) : part,
  ),
});

export const patchPartSelected = (
  draft: DmsDraft,
  partId: string,
  patch: Record<string, unknown>,
): DmsDraft =>
  patchPart(draft, partId, (part) => ({
    ...part,
    selected: { ...part.selected, ...patch },
  }));

/** The fabric code lives on `selected.FABRIC.data.code`, or on `selected.FABRIC` itself when typed. */
export const patchFabricCode = (
  draft: DmsDraft,
  partId: string,
  code: string,
  special: boolean,
): DmsDraft =>
  patchPart(draft, partId, (part) => ({
    ...part,
    selected: {
      ...part.selected,
      FABRIC: special
        ? code
        : {
            ...(part.selected.FABRIC as Record<string, unknown>),
            data: {
              ...((part.selected.FABRIC as { data?: Record<string, unknown> })
                .data ?? {}),
              code,
            },
          },
    },
  }));

export const patchMeasurement = (
  draft: DmsDraft,
  partId: string,
  index: number,
  patch: Partial<Measurement>,
): DmsDraft =>
  patchPart(draft, partId, (part) => ({
    ...part,
    measurements: (part.measurements ?? []).map((measurement, i) =>
      i === index ? { ...measurement, ...patch } : measurement,
    ),
  }));

/** The first check is stamped, a later one keeps it. */
export const toggleValidationField = (
  draft: DmsDraft,
  field: string,
  checked: boolean,
  userId: string,
  now = new Date(),
): DmsDraft => {
  const product = draft.product ?? {};
  const stampKey = `${field}Checked`;
  const patch: Record<string, unknown> = { [field]: checked };
  if (!product[stampKey])
    patch[stampKey] = checked ? { user: userId, date: now } : null;
  return patchProduct(draft, patch);
};

const COUNT_PATTERN = /^\d*$/;

export interface DmsFormCheck {
  /** Translation key of the message under the table, null when nothing blocks. */
  errorKey: string | null;
  invalidParts: ReadonlySet<string>;
  orderIdMissing: boolean;
}

/**
 * Only a
 * part size outside the model sizes or a count that is not a whole number
 * blocks the save; a missing order id is only underlined in red.
 */
export const checkDmsForm = (
  draft: DmsDraft,
  modelSizes: ReadonlyMap<string, string> | undefined,
): DmsFormCheck => {
  const invalidParts = new Set<string>();
  for (const part of orderParts(draft)) {
    const size = String(part.selected.size ?? '');
    const count = part.count == null ? '' : String(part.count);
    if (size !== '' && !partSizePattern(part, modelSizes).test(size))
      invalidParts.add(part.id);
    if (count !== '' && (!COUNT_PATTERN.test(count) || Number(count) < 0))
      invalidParts.add(part.id);
  }
  return {
    errorKey: invalidParts.size > 0 ? 'orders.pleaseFillFormCorrectly' : null,
    invalidParts,
    orderIdMissing: !draft.externalOrderId,
  };
};

/** The whole draft, the flags as booleans. */
export const toSavedDmsOrder = (draft: DmsDraft): Order => ({
  ...draft,
  purchaseStatus: isTruthyFlag(draft.purchaseStatus),
  priority: isTruthyFlag(draft.priority),
});
